import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./DevSecOps.css";
import Testimonial from "../../../Testimonial/Testimonial";

const DevSecOps = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>DevSecOps Solutions | Secure Software Delivery Pipeline</title>
        <meta
          name="description"
          content="Implement DevSecOps practices with our comprehensive security-focused development pipeline. Explore our technology stack for secure, automated software delivery."
        />
        <meta
          name="keywords"
          content="DevSecOps, secure development, CI/CD pipeline, security automation, infrastructure as code, container security, cloud security, compliance automation"
        />
      </Helmet>

      <div className="mk-devsecops-container">
        <section className="mk-devsecops-hero">
          <div className="mk-hero-content">
            <h1 className="mk-hero-title mk-animate-fade-in">
              DevSecOps Solutions
            </h1>
            <p className="mk-hero-subtitle mk-animate-slide-up">
              Integrating security into every phase of your development
              lifecycle
            </p>
            <a href="/contact-us">
              <button className="mk-cta-button mk-animate-pulse">
                Get Started
              </button>
            </a>
          </div>
          <div className="mk-hero-visual mk-animate-float">
            <div className="mk-security-shield"></div>
          </div>
        </section>

        <section className="mk-devsecops-intro">
          <div className="mk-section-header">
            <h2 className="mk-section-title">What is DevSecOps?</h2>
            <div className="mk-title-underline"></div>
          </div>
          <p className="mk-intro-text">
            DevSecOps is a culture shift in software development that integrates
            security practices within the DevOps process. It creates a 'Security
            as Code' culture without sacrificing agility, ensuring that security
            is a shared responsibility throughout the entire IT lifecycle.
          </p>
        </section>

        <section className="mk-devsecops-benefits">
          <div className="mk-section-header">
            <h2 className="mk-section-title">Key Benefits</h2>
            <div className="mk-title-underline"></div>
          </div>
          <div className="mk-benefits-grid">
            <div className="mk-benefit-card mk-animate-card">
              <div className="mk-benefit-icon">🔒</div>
              <h3>Enhanced Security</h3>
              <p>
                Identify and remediate vulnerabilities early in the development
                process.
              </p>
            </div>
            <div className="mk-benefit-card mk-animate-card">
              <div className="mk-benefit-icon">⚡</div>
              <h3>Faster Delivery</h3>
              <p>
                Automate security testing to maintain speed and agility in
                deployments.
              </p>
            </div>
            <div className="mk-benefit-card mk-animate-card">
              <div className="mk-benefit-icon">💰</div>
              <h3>Cost Reduction</h3>
              <p>
                Fix security issues early when they're less expensive to
                address.
              </p>
            </div>
            <div className="mk-benefit-card mk-animate-card">
              <div className="mk-benefit-icon">🤝</div>
              <h3>Collaboration</h3>
              <p>
                Break down silos between development, operations, and security
                teams.
              </p>
            </div>
          </div>
        </section>

        <section className="mk-devsecops-approach">
          <div className="mk-section-header">
            <h2 className="mk-section-title">Our DevSecOps Approach</h2>
            <div className="mk-title-underline"></div>
          </div>
          <div className="mk-approach-steps">
            <div className="mk-approach-step mk-animate-slide-left">
              <div className="mk-step-number">1</div>
              <div className="mk-step-content">
                <h3>Plan & Assess</h3>
                <p>
                  Evaluate your current workflow and identify security
                  integration points.
                </p>
              </div>
            </div>
            <div className="mk-approach-step mk-animate-slide-right">
              <div className="mk-step-number">2</div>
              <div className="mk-step-content">
                <h3>Implement & Automate</h3>
                <p>
                  Integrate security tools and automate testing within your
                  CI/CD pipeline.
                </p>
              </div>
            </div>
            <div className="mk-approach-step mk-animate-slide-left">
              <div className="mk-step-number">3</div>
              <div className="mk-step-content">
                <h3>Monitor & Improve</h3>
                <p>
                  Continuously monitor security posture and refine processes.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="mk-devsecops-cta">
          <h2 className="mk-cta-title">
            Ready to Secure Your Development Pipeline?
          </h2>
          <p className="mk-cta-text">
            Contact us today to discuss how our DevSecOps solutions can protect
            your applications.
          </p>
          <a
            href="https://wa.me/919155599968?text=Hi!%20I%20am%20looking%20for%20DevSecOps%20consultation%20to%20improve%20CI%2FCD%20security%2C%20compliance%2C%20and%20automation.%20Please%20schedule%20a%20call%20with%20your%20experts."
            target="_blank"
            rel="noreferrer"
          >
            <button className="cloud-contact-button">
              Schedule a Consultation
            </button>
          </a>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevSecOps;
